import React, { useState, useEffect } from 'react';
import { History, Loader2, Filter, Calendar, MapPin, CheckCircle, XCircle } from 'lucide-react';
import { ContextualTour } from '../../components/ContextualTour';
import { API } from '../../config/apiBase';


const HISTORICO_STEPS = [
    { target: '[data-testid="meu-histórico"]', title: 'Historico de Ponto', content: 'Consulte todas as suas marcacoes por dia, semana ou mes. Veja horas trabalhadas e pendencias.' },
];

export default function MeuHistóricoPage() {
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('all');

    useEffect(() => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;
        fetch(`${API}/ponto/registros`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json()).then(data => { setRegistros(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    // Filter by period
    const now = new Date();
    const filtered = registros.filter(r => {
        if (period === 'all') return true;
        const d = new Date(r.timestamp_server || r.timestamp);
        if (period === 'today') return d.toDateString() === now.toDateString();
        if (period === 'week') { const w = new Date(now); w.setDate(w.getDate() - 7); return d >= w; }
        if (period === 'month') { const m = new Date(now); m.setMonth(m.getMonth() - 1); return d >= m; }
        return true;
    });

    // Group by date
    const grouped = filtered.reduce((acc, r) => {
        const date = (r.timestamp_server || r.timestamp || '').split('T')[0];
        if (!acc[date]) acc[date] = [];
        acc[date].push(r);
        return acc;
    }, {});

    const totalEntradas = filtered.filter(r => r.tipo === 'entrada').length;
    const totalSaídas = filtered.filter(r => r.tipo === 'saída').length;

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="space-y-5" data-testid="meu-histórico">
            <ContextualTour pageKey="meu-historico" steps={HISTORICO_STEPS} />
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl p-3.5 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: 'var(--text)' }}>{filtered.length}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Total</p>
                </div>
                <div className="rounded-xl p-3.5 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: '#10B981' }}>{totalEntradas}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Entradas</p>
                </div>
                <div className="rounded-xl p-3.5 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: '#EF4444' }}>{totalSaídas}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Saídas</p>
                </div>
            </div>

            {/* Period Filter */}
            <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {[
                    { key: 'today', label: 'Hoje' },
                    { key: 'week', label: 'Semana' },
                    { key: 'month', label: 'Mes' },
                    { key: 'all', label: 'Todos' },
                ].map((f) => (
                    <button key={f.key} onClick={() => setPeriod(f.key)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all"
                        style={{ background: period === f.key ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface)', border: '1px solid var(--border)', color: period === f.key ? 'var(--sv-accent)' : 'var(--muted)' }}
                        data-testid={`filter-${f.key}`}>
                        {f.label}
                    </button>
                ))}
            </div>

            {/* Records */}
            {Object.keys(grouped).length === 0 ? (
                <div className="rounded-xl p-10 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <History size={36} className="mx-auto mb-3" style={{ color: 'var(--muted)' }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Nenhum registro</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Seus registros de ponto aparecerao aqui</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {Object.entries(grouped).sort(([a],[b]) => b.localeCompare(a)).map(([date, regs]) => (
                        <div key={date} className="rounded-xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: 'var(--surface-2)' }}>
                                <Calendar size={12} style={{ color: 'var(--sv-gold-primary)' }} />
                                <span className="text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }}>
                                    {new Date(date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                                </span>
                                <span className="text-[10px] ml-auto" style={{ color: 'var(--muted)' }}>{regs.length} reg.</span>
                            </div>
                            <div className="p-2 space-y-1">
                                {regs.map((r, i) => (
                                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                                        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: r.tipo === 'entrada' ? '#10B981' : '#EF4444' }} />
                                        <span className="text-xs font-medium flex-1" style={{ color: 'var(--text)' }}>
                                            {r.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                                        </span>
                                        {r.geo && <MapPin size={10} style={{ color: 'var(--muted)' }} />}
                                        <span className="text-[10px]" style={{ color: 'var(--muted)' }}>
                                            {new Date(r.timestamp_server || r.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
